import React, { useState, useRef, useEffect, useMemo, useCallback, useContext } from 'react';
import {
    CircularProgress,
    Avatar,
    IconButton,
    Popover,
    Switch,
    Tooltip,
    Box,
    FormControlLabel,
    MenuItem,
    ListItemIcon,
    Divider,
    Typography
} from '@mui/material';
import { useMediaQuery, useTheme } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Settings } from "@mui/icons-material";
import AddIcon from '@mui/icons-material/Add';
import MapsUgcRoundedIcon from '@mui/icons-material/MapsUgcRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';

import { config } from '@config';
import { reportError } from '../../lib/api/ErrorReporter';
import { useCustomThemeContext } from '../../lib/contexts/ThemeContext';
import VideoCallModal from '../../components/VideoCallModal/VideoCallModal';
import './TextChat.css';
import { useUserInfoContext } from '../../lib/contexts/UserInfoContext';
import { useChatSessionsContext } from '../../lib/contexts/ChatSessionsContext';
import { useChat } from '../../lib/hooks/UseChat';
import { usePersona } from '../../lib/hooks/UsePersona';
import { ChatSession, LinkedChatStream, Message as GlobalMessage, ChatUiMessage } from '../../lib/interfaces/Chat';
import type { Persona } from '../../lib/api/Persona';
import { getPersonaDisplayName } from '../../lib/helpers/personaDisplay';
import { canAccessPersona, canAccessCall, isSuperAdmin, isAnyAdmin, canViewDetailedAgenticWorkflow, USERS } from '@common/domain/auth';
import type { FeedbackCategoryId } from '@common/domain/types';
import { groupPersonasByBaseName, parsePersonaVariantName } from '../../lib/helpers/personaVariants';
import {
    SendIcon,
} from '../../components/Chat/ChatIcons';
import { consumeChatStream, ChatStreamError } from '../../lib/helpers/chatStream';
import { trackMessageSent } from '../../lib/analytics/chatAnalytics';
import { postTts, postTtsStream } from '../../lib/api/Tts';
import { profileContext } from "../../pages/settings/Profile";
import { stripCitationsForSpeech } from '../../lib/helpers/chatUtils';

import { SpeechRecognitionInstance, SpeechRecognitionEventLike, SpeechRecognitionErrorEventLike } from './types';
import { ChatPane } from './ChatPane';
import {
    SCOUT_AVATAR_IMAGE_URL,
    SCOUT_EXTENDED_PREFIX,
    SCOUT_PERSONA,
    SCOUT_PERSONA_ID,
    type ScoutGoalMetadata,
    type ScopedScoutStatusGoal,
    buildScoutGoalChecklistSnapshot,
    includeScoutPersona,
    isScoutPersona,
    latestScoutGoalMetadata,
    markScoutDiscoveryComplete,
    persistScoutGoal,
    readPersistedScoutGoal,
    readScoutDiscoveryComplete,
    resolveScoutGuideProfile,
    scoutGoalScopeKey,
    selectScopedScoutStatusGoal,
} from './scout';
import { getDiscoveryStatus } from '../../lib/api/Discovery';
import {
    AdminGoalChecklistBar,
    AdminGoalChecklistVisibilityControl,
    persistGoalChecklistVisibility,
    readGoalChecklistVisibility,
} from './goal-checklist/AdminGoalChecklistBar';
import { latestGoalChecklistSnapshot } from './goal-checklist/goalChecklist';

/**
 * Feeds MP3 chunks from a fetch body into an MSE SourceBuffer; ends MediaSource when the reader completes.
 */
async function appendMpegChunksToSourceBuffer(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    mediaSource: MediaSource,
    sourceBuffer: SourceBuffer,
    signal: AbortSignal,
): Promise<void> {
    const queue: Uint8Array[] = [];
    let readerDone = false;
    let settled = false;

    return new Promise((resolve, reject) => {
        const cleanup = () => {
            sourceBuffer.removeEventListener('updateend', onUpdateEnd);
        };

        const finishOk = () => {
            if (settled) return;
            settled = true;
            cleanup();
            resolve();
        };

        const finishErr = (e: unknown) => {
            if (settled) return;
            settled = true;
            cleanup();
            reject(e instanceof Error ? e : new Error(String(e)));
        };

        const tryAppend = () => {
            if (signal.aborted) {
                finishOk();
                return;
            }
            if (sourceBuffer.updating) {
                return;
            }
            if (queue.length > 0) {
                const next = queue.shift()!;
                try {
                    const normalized = new Uint8Array(next.byteLength);
                    normalized.set(next);
                    sourceBuffer.appendBuffer(normalized);
                } catch (e) {
                    finishErr(e);
                }
                return;
            }
            if (readerDone) {
                if (mediaSource.readyState === 'open') {
                    try {
                        mediaSource.endOfStream();
                    } catch {
                        /* ignore */
                    }
                }
                finishOk();
            }
        };

        const onUpdateEnd = () => {
            tryAppend();
        };

        sourceBuffer.addEventListener('updateend', onUpdateEnd);

        void (async () => {
            try {
                while (!signal.aborted) {
                    const { done, value } = await reader.read();
                    if (done) {
                        readerDone = true;
                        tryAppend();
                        break;
                    }
                    if (value && value.byteLength > 0) {
                        queue.push(
                            value.byteOffset === 0 && value.byteLength === value.buffer.byteLength
                                ? value
                                : new Uint8Array(value),
                        );
                    }
                    tryAppend();
                }
            } catch (e) {
                finishErr(e);
            }
        })();

        tryAppend();
    });
}

const CHAT_STREAM_TIMEOUT_MS = 45000;
const CHAT_HISTORY_LOAD_TIMEOUT_MS = 10000;
const COMPARISON_STREAM_ID = 'comparison';
const COMPARISON_STREAM_LABEL = 'Comparison chat';
const DISCOVERY_AUTO_START_STORAGE_KEY = 'discoveryAutoStartEnabled:v1';
const DISCOVERY_TEST_RUN_ENABLED_STORAGE_KEY = 'discoveryTestRunEnabled:v1';
const DISCOVERY_TEST_RUN_ID_STORAGE_KEY = 'discoveryTestRunId:v1';
const DEFAULT_DISCOVERY_STATUS_TARGET_PERSONA_ID = 'chip_default';

function getComparisonStreamChatId(parentChatId: string, comparisonStreamId: string): string {
    return `${parentChatId}--stream--${comparisonStreamId}`;
}

function createDiscoveryTestRunId(): string {
    return `run-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timeout = window.setTimeout(() => {
            reject(new Error(`${label} timed out after ${timeoutMs}ms`));
        }, timeoutMs);

        promise.then(
            (value) => {
                window.clearTimeout(timeout);
                resolve(value);
            },
            (error) => {
                window.clearTimeout(timeout);
                reject(error);
            },
        );
    });
}

function describeChatError(error: unknown): string {
    if (error instanceof DOMException && error.name === 'AbortError') {
        return 'request timed out or was aborted';
    }
    if (error instanceof Error && error.message.trim()) {
        return error.message;
    }
    if (typeof error === 'string' && error.trim()) {
        return error;
    }
    return 'unknown stream error';
}

function buildScoutGoalFromDiscoveryStatus(status: Awaited<ReturnType<typeof getDiscoveryStatus>>): ScoutGoalMetadata | null {
    if (!status) return null;
    return {
        decision: status.status === 'complete' ? 'actively_pursue' : 'keep_in_background',
        runtime: {
            libraryGoalName: 'User Discovery',
            skipped: false,
            observability: {
                status: status.status === 'complete' ? 'satisfied' : 'active',
                progressPercentage: status.progressPercentage,
                satisfiedCriteria: status.satisfiedCriteria,
                partialCriteria: status.partialCriteria,
                missingCriteria: status.missingCriteria,
                blockedCriteria: [],
                deferredCriteria: [],
                currentCriterionId: status.missingRequiredCriteria[0] ?? null,
            },
        },
    };
}

function scoutGoalProgress(goal: ScoutGoalMetadata | null | undefined): number {
    return goal?.runtime?.observability?.progressPercentage ?? -1;
}

declare global {
    interface Window {
        SpeechRecognition?: new () => SpeechRecognitionInstance;
        webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
    }
}

// ================================================================
// Main Page Component
// ================================================================
export interface TextChatPageProps {
    presentation?: 'page' | 'workspace';
    lockedPersona?: Persona;
    /** Lesson-opening text shown as the first assistant bubble in workspace chat sessions. */
    openingPreamble?: string;
    workspaceConversationBoundaryKey?: string;
    workspaceChatSessionId?: string;
    studySessionId?: string;
    chapterSlug?: string;
    allowWorkspaceNewChat?: boolean;
    onWorkspaceNewChat?: () => void;
    onTeachingInteraction?: () => void;
    onChatSessionIdentified?: (chatId: string) => boolean | void | Promise<boolean | void>;
}

const TextChatPage: React.FC<TextChatPageProps> = ({
    presentation = 'page',
    lockedPersona,
    openingPreamble,
    workspaceConversationBoundaryKey,
    workspaceChatSessionId,
    chapterSlug,
    allowWorkspaceNewChat = false,
    onWorkspaceNewChat,
    onTeachingInteraction,
    onChatSessionIdentified,
}) => {
    const isWorkspacePresentation = presentation === 'workspace';
    const { urlChatSession } = useParams<{ urlChatSession: string }>();
    const { userInfo } = useUserInfoContext();
    const {
        selectedChatSession,
        updateSelectedChatSession,
        addNewChatSession,
        resetSelectedChatSession,
        chatSessions,
        setChatSessions,
        allMessages,
        setMessages,
        persona: selectedPersona,
        updatePersona
    } = useChatSessionsContext();
    const { getChatMessages, postMessageStream, putRating } = useChat();
    const { getPersonas } = usePersona();

    const navigate = useNavigate();
    const navigateToChat = useCallback((chatId?: string, replace?: boolean) => {
        if (isWorkspacePresentation) return;
        const path = chatId ? `/text-chat/${chatId}` : '/text-chat';
        if (replace === undefined) navigate(path);
        else navigate(path, { replace });
    }, [isWorkspacePresentation, navigate]);
    const { isDarkMode, toggleDarkMode } = useCustomThemeContext();
    const [inputValue, setInputValue] = useState('');
    const [chatToolsAnchorEl, setChatToolsAnchorEl] = useState<HTMLElement | null>(null);
    const [companionMenuAnchorEl, setCompanionMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [expandedCompanionGroups, setExpandedCompanionGroups] = useState<Set<string>>(new Set());
    const [videoCallOpen, setVideoCallOpen] = useState(false);
    const [forceWebSearch, setForceWebSearch] = useState(false);
    const [useSynapse, setUseSynapse] = useState(false);
    const [useSynapseVariant, setUseSynapseVariant] = useState(false);
    const [autoStartDiscovery, setAutoStartDiscoveryState] = useState(() => {
        try {
            if (typeof window === 'undefined') return false;
            return window.localStorage.getItem(DISCOVERY_AUTO_START_STORAGE_KEY) === '1';
        } catch {
            return false;
        }
    });
    const [autoStartDiscoveryStatus, setAutoStartDiscoveryStatus] = useState<'idle' | 'checking' | 'recommended' | 'complete' | 'skipped' | 'disabled' | 'error'>('idle');
    const [autoStartTargetPersona, setAutoStartTargetPersona] = useState<Persona | null>(null);
    const [pendingDiscoveryGuidePersona, setPendingDiscoveryGuidePersona] = useState<Persona | null>(null);
    const [discoveryTestRunEnabled, setDiscoveryTestRunEnabledState] = useState(() => {
        try {
            if (typeof window === 'undefined') return false;
            return window.localStorage.getItem(DISCOVERY_TEST_RUN_ENABLED_STORAGE_KEY) === '1';
        } catch {
            return false;
        }
    });
    const [discoveryTestRunId, setDiscoveryTestRunId] = useState(() => {
        try {
            if (typeof window === 'undefined') return createDiscoveryTestRunId();
            return window.localStorage.getItem(DISCOVERY_TEST_RUN_ID_STORAGE_KEY) || createDiscoveryTestRunId();
        } catch {
            return createDiscoveryTestRunId();
        }
    });
    const [comparisonForceWebSearch, setComparisonForceWebSearch] = useState(false);
    const [linkedChatStreams, setLinkedChatStreams] = useState<LinkedChatStream[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [chatPersistenceStatus, setChatPersistenceStatus] = useState<Record<string, 'sending' | 'persisted' | 'failed'>>({});
    const [isSessionLoading, setIsSessionLoading] = useState(false);
    const [showClassification, setShowClassification] = useState(true);
    // Mirrors the API's group-based redaction: accounts outside the allowlist get an
    // abstracted processing animation instead of the detailed agentic workflow trace.
    const showWorkflowSteps = canViewDetailedAgenticWorkflow(userInfo?.groups);
    const [showGoalChecklist, setShowGoalChecklistState] = useState(readGoalChecklistVisibility);
    const [personas, setPersonas] = useState<Persona[]>([]);
    

    const [isRecording, setIsRecording] = useState(false);
    const [recordingSupported, setRecordingSupported] = useState(false);
    const [, setVoiceError] = useState('')
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const chatHeaderRef = useRef<HTMLElement | null>(null);
    const inputContainerRef = useRef<HTMLDivElement | null>(null);
    const lastLoadedSessionId = useRef<string | null>(null);
    const discoveryAutoStartAttemptRef = useRef<string | null>(null);
    const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
    const finalTranscriptRef = useRef('');
    const inputOriginRef = useRef<'text' | 'voice'>('text');
    const suppressComposerFocusInteractionRef = useRef(false);
    const lockedPersonaRef = useRef(lockedPersona);
    const updatePersonaRef = useRef(updatePersona);
    const workspaceLockStateRef = useRef<{ active: boolean; personaId: string | null }>({
        active: false,
        personaId: null,
    });
    const workspaceChatOwnershipRef = useRef<{ identity: string | null; chatIds: Set<string> }>({
        identity: null,
        chatIds: new Set(),
    });
    const identifiedChatIdsRef = useRef<Set<string>>(new Set());
    const linkingChatIdsRef = useRef<Set<string>>(new Set());
    lockedPersonaRef.current = lockedPersona;
    updatePersonaRef.current = updatePersona;

    const workspaceIdentity = isWorkspacePresentation
        ? `${lockedPersona?.id ?? '__missing_persona__'}:${workspaceConversationBoundaryKey ?? '__default_boundary__'}`
        : null;
    if (workspaceChatOwnershipRef.current.identity !== workspaceIdentity) {
        workspaceChatOwnershipRef.current = { identity: workspaceIdentity, chatIds: new Set() };
    }
    const activeChatSessionId = !isWorkspacePresentation
        || (selectedChatSession != null && workspaceChatOwnershipRef.current.chatIds.has(selectedChatSession))
        ? selectedChatSession
        : null;
    const activeChatSessionIdRef = useRef(activeChatSessionId);
    activeChatSessionIdRef.current = activeChatSessionId;

    const workspacePersonaReady =
        !isWorkspacePresentation ||
        Boolean(lockedPersona?.id && selectedPersona?.id === lockedPersona.id);

    const reportTeachingInteraction = useCallback(() => {
        onTeachingInteraction?.();
    }, [onTeachingInteraction]);

    const focusComposer = useCallback(() => {
        const composer = inputRef.current;
        if (!composer || document.activeElement === composer) return;

        suppressComposerFocusInteractionRef.current = true;
        try {
            composer.focus();
        } finally {
            suppressComposerFocusInteractionRef.current = false;
        }
    }, []);

    const handleComposerFocus = useCallback(() => {
        if (!suppressComposerFocusInteractionRef.current) {
            reportTeachingInteraction();
        }
    }, [reportTeachingInteraction]);

    const handleMessagesKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        if (['ArrowDown', 'ArrowUp', 'End', 'Home', 'PageDown', 'PageUp', ' '].includes(event.key)) {
            reportTeachingInteraction();
        }
    }, [reportTeachingInteraction]);

    const [isTtsEnabled] = useState(true);
    const [currentlyPlayingId, setCurrentlyPlayingId] = useState<string | null>(null);
    const speechUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const currentObjectUrlRef = useRef<string | null>(null);
    const ttsAbortControllerRef = useRef<AbortController | null>(null);
    const ttsAudioListenersRef = useRef<{
        onEnded: (() => void) | null;
        onError: (() => void) | null;
    }>({ onEnded: null, onError: null });
    const ttsMseRef = useRef<{ mediaSource: MediaSource | null; sourceBuffer: SourceBuffer | null }>({
        mediaSource: null,
        sourceBuffer: null,
    });

    const mapToLocalMessage = (msg: GlobalMessage, index: number): ChatUiMessage => ({
        id: msg.exchangeId ? `${msg.exchangeId}-${msg.fromUser ? 'user' : 'assistant'}` : `${msg.fromUser ? 'user' : 'assistant'}-${index}`,
        role: msg.fromUser ? 'user' : 'assistant',
        content: msg.message,
        sources: msg.sources,
        seeds: msg.seeds,
        retrievedChunkIds: msg.retrievedChunkIds,
        usedChunkIds: msg.usedChunkIds,
        safetyRoute: msg.safetyRoute,
        isStreaming: msg.isStreaming,
        interrupted: msg.interrupted,
        agenticProgress: (msg as any).agenticProgress,
        agenticProgressTrail: (msg as any).agenticProgressTrail,
        exchangeId: msg.exchangeId,
        rating: msg.rating,
        comment: msg.comment,
        feedbackMode: msg.feedbackMode,
        feedbackCategory: msg.feedbackCategory,
        responseMode: msg.responseMode,
        workflowMetadata: (msg as any).workflowMetadata,
        safetyClassification: (msg as any).safetyClassification,
        retrievalDecision: (msg as any).retrievalDecision,
        conversationMomentum: (msg as any).conversationMomentum,
        alignment: (msg as any).alignment,
        retrievalSummary: (msg as any).retrievalSummary,
        toolTrace: msg.toolTrace,
        useSynapse: msg.useSynapse,
        goal: msg.goal,
        comparisonRunId: msg.comparisonRunId,
        comparisonStreamId: msg.comparisonStreamId,
        comparisonStreamLabel: msg.comparisonStreamLabel,
        parentExchangeId: msg.parentExchangeId,
        streamChatId: msg.streamChatId,
        isVariant: msg.isVariant,
        settings: msg.settings,
    });

    const mainPaneMessages = useMemo(() => {
        const msgs = activeChatSessionId ? allMessages[activeChatSessionId] || [] : [];
        return msgs.map(mapToLocalMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeChatSessionId, allMessages]);

    // The workspace's lesson-opening preamble is a local-only transcript bubble.
    const prependWorkspacePreamble = useCallback((messages: ChatUiMessage[]) => {
        if (!isWorkspacePresentation || !openingPreamble) return messages;
        const preambleMessage: ChatUiMessage = {
            id: 'workspace-preamble',
            role: 'assistant',
            content: openingPreamble,
            isPreamble: true,
        };
        return [preambleMessage, ...messages];
    }, [isWorkspacePresentation, openingPreamble]);

    const chatPaneMessages = useMemo(
        () => prependWorkspacePreamble(mainPaneMessages),
        [mainPaneMessages, prependWorkspacePreamble],
    );

    const comparisonPaneMessages = useMemo(() => {
        const comparisonStream = linkedChatStreams.find(s => s.comparisonStreamId === COMPARISON_STREAM_ID);
        const msgs = comparisonStream ? allMessages[comparisonStream.chatId] || [] : [];
        return msgs.map(mapToLocalMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allMessages, linkedChatStreams]);

    const comparisonChatPaneMessages = useMemo(
        () => prependWorkspacePreamble(comparisonPaneMessages),
        [comparisonPaneMessages, prependWorkspacePreamble],
    );

    const [personaLoadError, setPersonaLoadError] = useState(false);

    const loadPersonas = useCallback(() => {
        if (!isWorkspacePresentation && userInfo) {
            setPersonaLoadError(false);
            return getPersonas()
                .then(fetchedPersonas => {
                    const filtered = fetchedPersonas.filter(p => canAccessPersona(p, userInfo));
                    setPersonas(config.companyName !== "Gargantua" ? filtered : includeScoutPersona(filtered));
                    setPersonaLoadError(false);
                })
                .catch(err => {
                    console.error("Failed to load personas:", err);
                    setPersonaLoadError(true);
                });
        }
    }, [isWorkspacePresentation, userInfo, getPersonas]);

    // Load personas on mount
    useEffect(() => {
        let cancelled = false;
        if (!isWorkspacePresentation && userInfo) {
            getPersonas()
                .then(fetchedPersonas => {
                    if (cancelled) return;
                    const filtered = fetchedPersonas.filter(p => canAccessPersona(p, userInfo));
                    setPersonas(config.companyName !== "Gargantua" ? filtered : includeScoutPersona(filtered));
                    setPersonaLoadError(false);
                })
                .catch(err => {
                    if (!cancelled) {
                        console.error("Failed to load personas:", err);
                        setPersonaLoadError(true);
                    }
                });
        }
        return () => {
            cancelled = true;
        };
    }, [isWorkspacePresentation, userInfo, getPersonas]);

    useEffect(() => {
        const lockedPersonaId = lockedPersonaRef.current?.id ?? null;
        const previousLockState = workspaceLockStateRef.current;
        const lockChanged =
            previousLockState.active !== isWorkspacePresentation ||
            previousLockState.personaId !== lockedPersonaId;
        workspaceLockStateRef.current = {
            active: isWorkspacePresentation,
            personaId: lockedPersonaId,
        };

        if (!isWorkspacePresentation) return;
        if (!lockedPersonaId) {
            if (lockChanged || selectedPersona?.id) updatePersonaRef.current(undefined);
            return;
        }
        if (lockChanged || selectedPersona?.id !== lockedPersonaId) {
            updatePersonaRef.current(lockedPersonaRef.current);
        }
    }, [isWorkspacePresentation, lockedPersona?.id, selectedPersona?.id]);

    useEffect(() => {
        if (!isWorkspacePresentation || !workspaceChatSessionId || !workspacePersonaReady) return;
        workspaceChatOwnershipRef.current.chatIds.add(workspaceChatSessionId);
        if (selectedChatSession !== workspaceChatSessionId) {
            updateSelectedChatSession(workspaceChatSessionId);
        }
    }, [
        isWorkspacePresentation,
        selectedChatSession,
        updateSelectedChatSession,
        workspaceChatSessionId,
        workspacePersonaReady,
    ]);

    const setAutoStartDiscovery = useCallback((enabled: boolean) => {
        setAutoStartDiscoveryState(enabled);
        discoveryAutoStartAttemptRef.current = null;
        setAutoStartDiscoveryStatus('idle');
        try {
            if (enabled) {
                window.localStorage.setItem(DISCOVERY_AUTO_START_STORAGE_KEY, '1');
            } else {
                window.localStorage.removeItem(DISCOVERY_AUTO_START_STORAGE_KEY);
            }
        } catch {
            // Local storage is optional; the in-memory toggle still works for this page load.
        }
    }, []);

    const setShowGoalChecklist = useCallback((visible: boolean) => {
        setShowGoalChecklistState(visible);
        persistGoalChecklistVisibility(visible);
    }, []);

    const resetDiscoveryAutoStartCheck = useCallback(() => {
        discoveryAutoStartAttemptRef.current = null;
        setAutoStartDiscoveryStatus('idle');
        setPendingDiscoveryGuidePersona(null);
        setAutoStartTargetPersona(null);
    }, []);

    const setDiscoveryTestRunEnabled = useCallback((enabled: boolean) => {
        setDiscoveryTestRunEnabledState(enabled);
        resetDiscoveryAutoStartCheck();
        try {
            if (enabled) {
                window.localStorage.setItem(DISCOVERY_TEST_RUN_ENABLED_STORAGE_KEY, '1');
                window.localStorage.setItem(DISCOVERY_TEST_RUN_ID_STORAGE_KEY, discoveryTestRunId);
            } else {
                window.localStorage.removeItem(DISCOVERY_TEST_RUN_ENABLED_STORAGE_KEY);
            }
        } catch {
            // Local storage is optional; the in-memory toggle still works for this page load.
        }
    }, [discoveryTestRunId, resetDiscoveryAutoStartCheck]);

    const createNewDiscoveryTestRun = useCallback(() => {
        const nextRunId = createDiscoveryTestRunId();
        setDiscoveryTestRunId(nextRunId);
        resetDiscoveryAutoStartCheck();
        if (selectedChatSession) {
            setMessages([], selectedChatSession);
        }
        resetSelectedChatSession();
        setLinkedChatStreams([]);
        setInputValue('');
        lastLoadedSessionId.current = null;
        navigateToChat(undefined, true);
        try {
            window.localStorage.setItem(DISCOVERY_TEST_RUN_ID_STORAGE_KEY, nextRunId);
            window.localStorage.setItem(DISCOVERY_TEST_RUN_ENABLED_STORAGE_KEY, '1');
        } catch {
            // Local storage is optional; the in-memory run id still works for this page load.
        }
        setDiscoveryTestRunEnabledState(true);
    }, [navigateToChat, resetDiscoveryAutoStartCheck, resetSelectedChatSession, selectedChatSession, setMessages]);

    const activeDiscoveryTestRunId = discoveryTestRunEnabled ? discoveryTestRunId : undefined;

    const selectPersona = useCallback((persona: Persona) => {
        if (isWorkspacePresentation) return;
        const targetIsScout = isScoutPersona(persona);
        const leavingScout = isScoutPersona(selectedPersona) && !targetIsScout;
        updatePersona(persona);
        if (leavingScout) {
            setUseSynapse(true);
        }
        setPendingDiscoveryGuidePersona(null);
        setLinkedChatStreams([]);
        setInputValue('');
        lastLoadedSessionId.current = null;
        discoveryAutoStartAttemptRef.current = null;
        navigateToChat(undefined, true);
        if (!targetIsScout) {
            setAutoStartTargetPersona(null);
        }
        focusComposer();
    }, [focusComposer, isWorkspacePresentation, navigateToChat, selectedPersona, updatePersona]);

    const buildDiscoveryPersona = useCallback((guideProfileId: string): Persona => {
        const baseScout = personas.find((persona) => persona.id === SCOUT_PERSONA_ID) ?? SCOUT_PERSONA;
        const profile = resolveScoutGuideProfile({ discoveryGuideProfileId: guideProfileId });
        return {
            ...baseScout,
            tenantId: profile.tenantId,
            category: profile.category,
            name: profile.displayName,
            title: profile.title,
            imageURL: profile.imageURL,
            personality: profile.personality,
            discoveryGuideProfileId: profile.id,
        };
    }, [personas]);

    // --- Scout discovery state ---
    const isScoutMode = isScoutPersona(selectedPersona);
    const scoutGuideProfile = useMemo(
        () => resolveScoutGuideProfile(selectedPersona),
        [selectedPersona]
    );
    const pendingDiscoveryGuideProfile = useMemo(
        () => pendingDiscoveryGuidePersona ? resolveScoutGuideProfile(pendingDiscoveryGuidePersona) : null,
        [pendingDiscoveryGuidePersona]
    );
    const scoutStatusScopeKey = scoutGoalScopeKey(
        selectedChatSession,
        selectedPersona?.id,
        activeDiscoveryTestRunId,
    );
    const [scoutStatusGoal, setScoutStatusGoal] = useState<ScopedScoutStatusGoal | null>(null);
    useEffect(() => {
        setScoutStatusGoal(null);
    }, [selectedChatSession, selectedPersona?.id, activeDiscoveryTestRunId]);
    const liveScoutGoal = useMemo(() => latestScoutGoalMetadata(mainPaneMessages), [mainPaneMessages]);
    const scopedScoutStatusGoal = selectScopedScoutStatusGoal(scoutStatusGoal, scoutStatusScopeKey);
    const scoutGoal = useMemo(
        () => {
            const persistedGoal = readPersistedScoutGoal(selectedChatSession, activeDiscoveryTestRunId);
            return [liveScoutGoal, scopedScoutStatusGoal, persistedGoal]
                .filter((goal): goal is ScoutGoalMetadata => Boolean(goal))
                .sort((a, b) => scoutGoalProgress(b) - scoutGoalProgress(a))[0] ?? null;
        },
        [activeDiscoveryTestRunId, liveScoutGoal, selectedChatSession, scopedScoutStatusGoal]
    );
    const liveGoalChecklist = useMemo(
        () => latestGoalChecklistSnapshot(mainPaneMessages),
        [mainPaneMessages],
    );
    const scoutGoalChecklist = useMemo(
        () => isScoutMode ? buildScoutGoalChecklistSnapshot(scoutGoal, mainPaneMessages.length) : null,
        [isScoutMode, mainPaneMessages.length, scoutGoal],
    );
    const goalChecklistSnapshot = liveGoalChecklist ?? scoutGoalChecklist;
    useEffect(() => {
        if (liveScoutGoal) persistScoutGoal(selectedChatSession, liveScoutGoal, activeDiscoveryTestRunId);
        const observability = liveScoutGoal?.runtime?.observability;
        if (observability?.status === 'satisfied' || (observability?.progressPercentage ?? 0) >= 100) {
            markScoutDiscoveryComplete(userInfo?.userId, activeDiscoveryTestRunId);
        }
    }, [activeDiscoveryTestRunId, liveScoutGoal, selectedChatSession, userInfo?.userId]);

    // Continuing with Scout extends collection for this chat session only.
    const [scoutExtended, setScoutExtended] = useState(false);
    useEffect(() => {
        try {
            setScoutExtended(
                Boolean(selectedChatSession) &&
                window.localStorage.getItem(`${SCOUT_EXTENDED_PREFIX}${selectedChatSession}`) === '1'
            );
        } catch {
            setScoutExtended(false);
        }
    }, [selectedChatSession]);
    const chooseScoutExtended = useCallback(() => {
        setScoutExtended(true);
        try {
            if (selectedChatSession) {
                window.localStorage.setItem(`${SCOUT_EXTENDED_PREFIX}${selectedChatSession}`, '1');
            }
        } catch { /* best effort */ }
    }, [selectedChatSession]);
    useEffect(() => {
        if (!isScoutMode || !userInfo || isLoading || isSessionLoading) {
            return;
        }

        let cancelled = false;
        const targetPersonaId = autoStartTargetPersona?.id || DEFAULT_DISCOVERY_STATUS_TARGET_PERSONA_ID;
        getDiscoveryStatus(userInfo, targetPersonaId, activeDiscoveryTestRunId)
            .then((status) => {
                if (cancelled || !status) return;
                const nextGoal = buildScoutGoalFromDiscoveryStatus(status);
                setScoutStatusGoal({ scopeKey: scoutStatusScopeKey, goal: nextGoal });
                if (nextGoal) {
                    persistScoutGoal(selectedChatSession, nextGoal, activeDiscoveryTestRunId);
                }
                if (status.status === 'complete') {
                    markScoutDiscoveryComplete(userInfo.userId, activeDiscoveryTestRunId);
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    console.warn('[discovery] Scout completion status check failed', error);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [
        isScoutMode,
        userInfo,
        isLoading,
        isSessionLoading,
        mainPaneMessages.length,
        autoStartTargetPersona?.id,
        activeDiscoveryTestRunId,
        selectedChatSession,
        scoutStatusScopeKey,
    ]);

    const scoutObservability = scoutGoal?.runtime?.observability;
    const scoutDiscoveryDone =
        scoutObservability?.status === 'satisfied' ||
        (scoutObservability?.progressPercentage ?? 0) >= 100;
    const scoutReturningUser = isScoutMode && readScoutDiscoveryComplete(userInfo?.userId, activeDiscoveryTestRunId);

    const [showScoutStarterQuestion, setShowScoutStarterQuestion] = useState(false);
    useEffect(() => {
        if (!isScoutMode || mainPaneMessages.length > 0 || isSessionLoading || scoutReturningUser) {
            setShowScoutStarterQuestion(false);
            return;
        }

        setShowScoutStarterQuestion(false);
        const timeoutId = window.setTimeout(() => {
            setShowScoutStarterQuestion(true);
        }, 5000);

        return () => window.clearTimeout(timeoutId);
    }, [isScoutMode, mainPaneMessages.length, isSessionLoading, scoutReturningUser]);

    useEffect(() => {
        if (
            !autoStartDiscovery ||
            !userInfo ||
            !selectedPersona?.id ||
            isWorkspacePresentation ||
            isScoutMode ||
            pendingDiscoveryGuidePersona ||
            isLoading ||
            isSessionLoading ||
            mainPaneMessages.length > 0
        ) {
            return;
        }

        const attemptKey = `${userInfo.userId}:${selectedChatSession ?? 'new'}:${selectedPersona.id}:${activeDiscoveryTestRunId ?? 'real'}`;
        if (discoveryAutoStartAttemptRef.current === attemptKey) {
            return;
        }
        discoveryAutoStartAttemptRef.current = attemptKey;

        let cancelled = false;
        setAutoStartDiscoveryStatus('checking');

        getDiscoveryStatus(userInfo, selectedPersona.id, activeDiscoveryTestRunId)
            .then((status) => {
                if (cancelled) return;
                if (!status) {
                    setAutoStartDiscoveryStatus('disabled');
                    return;
                }

                setAutoStartDiscoveryStatus(status.status);
                if (status.status !== 'recommended') {
                    setPendingDiscoveryGuidePersona(null);
                    return;
                }

                const targetPersona = selectedPersona;
                const guidePersona = buildDiscoveryPersona(status.guideProfileId);
                setAutoStartTargetPersona(targetPersona);
                setPendingDiscoveryGuidePersona(guidePersona);
            })
            .catch((error) => {
                if (cancelled) return;
                console.warn('[discovery] auto-start status check failed', error);
                setAutoStartDiscoveryStatus('error');
            });

        return () => {
            cancelled = true;
        };
    }, [
        autoStartDiscovery,
        userInfo,
        selectedPersona,
        isWorkspacePresentation,
        isScoutMode,
        pendingDiscoveryGuidePersona,
        isLoading,
        isSessionLoading,
        mainPaneMessages.length,
        selectedChatSession,
        buildDiscoveryPersona,
        activeDiscoveryTestRunId,
    ]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
    const canUseChatVariants = isAnyAdmin(userInfo?.groups);
    const hideChatTools = config.hideChatToolsForUsersOnly
        && userInfo?.groups?.length === 1
        && userInfo.groups.includes(USERS);
    const isSplitChatActive =
        canUseChatVariants &&
        (useSynapseVariant ||
            comparisonForceWebSearch ||
            linkedChatStreams.length > 0 ||
            comparisonPaneMessages.length > 0);

    // Prevent the document/body from scrolling on mobile.
    useEffect(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;

        if (!isMobile) return;

        const scrollY = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;

        const prevBodyOverflow = document.body.style.overflow;
        const prevBodyPosition = document.body.style.position;
        const prevBodyTop = document.body.style.top;
        const prevBodyWidth = document.body.style.width;

        document.documentElement.classList.add('text-chat-no-scroll');
        document.body.classList.add('text-chat-no-scroll');
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';

        return () => {
            document.body.style.overflow = prevBodyOverflow;
            document.body.style.position = prevBodyPosition;
            document.body.style.top = prevBodyTop;
            document.body.style.width = prevBodyWidth;
            document.documentElement.classList.remove('text-chat-no-scroll');
            document.body.classList.remove('text-chat-no-scroll');

            // Restore scroll position after unlocking fixed body
            try {
                window.scrollTo(0, scrollY);
            } catch (e) {
                console.warn('[text-chat] Failed to restore scroll position', e);
            }
        };
    }, [isMobile]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const SpeechRecognitionCtor = window.SpeechRecognition || window.webkitSpeechRecognition;
        setRecordingSupported(!!SpeechRecognitionCtor);
    }, []);

    useEffect(() => {
        if (!canUseChatVariants && useSynapseVariant) {
            setUseSynapseVariant(false);
        }
    }, [canUseChatVariants, useSynapseVariant]);

    // Update selected chat session from URL
    useEffect(() => {
        if (isWorkspacePresentation) return;
        if (urlChatSession) {
            if (urlChatSession !== selectedChatSession) {
                updateSelectedChatSession(urlChatSession);
            }
        } else if (selectedChatSession) {
            resetSelectedChatSession();
        }
    }, [isWorkspacePresentation, urlChatSession, selectedChatSession, updateSelectedChatSession, resetSelectedChatSession]);

    useEffect(() => {
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.onstart = null;
                recognitionRef.current.onresult = null;
                recognitionRef.current.onerror = null;
                recognitionRef.current.onend = null;
                recognitionRef.current.abort();
                recognitionRef.current = null;
            }
        };
    }, []);

    // Load messages when session changes
    useEffect(() => {
        let isCurrent = true;

        if (userInfo && activeChatSessionId && !isLoading && activeChatSessionId !== lastLoadedSessionId.current) {
            setIsSessionLoading(true);
            const selectedSession = chatSessions.find(session => session.chatInfo.chatId === activeChatSessionId);
            const sessionLinkedStreams = canUseChatVariants
                ? selectedSession?.chatInfo.linkedChatStreams || []
                : [];

            withTimeout(
                Promise.all([
                    getChatMessages(userInfo, activeChatSessionId),
                    ...sessionLinkedStreams.map(stream => getChatMessages(userInfo, stream.chatId)),
                ]),
                CHAT_HISTORY_LOAD_TIMEOUT_MS,
                `Loading conversation ${activeChatSessionId}`,
            ).then(([mainMsgs, ...streamMessageSets]) => {
                if (isCurrent) {
                    if (mainMsgs) {
                        setMessages(mainMsgs, activeChatSessionId);
                        sessionLinkedStreams.forEach((stream, index) => {
                            const streamMsgs = (streamMessageSets[index] || []).map(msg => ({
                                ...msg,
                                comparisonStreamId: stream.comparisonStreamId,
                                comparisonStreamLabel: stream.label,
                                streamChatId: stream.chatId,
                                isVariant: true,
                                settings: stream.settings,
                            }));
                            setMessages(streamMsgs, stream.chatId);
                        });

                        setLinkedChatStreams(sessionLinkedStreams);
                        const comparisonStream = sessionLinkedStreams.find(stream => stream.comparisonStreamId === COMPARISON_STREAM_ID);
                        setUseSynapseVariant(comparisonStream?.settings?.useSynapse === true);
                        setComparisonForceWebSearch(comparisonStream?.settings?.forceWebSearch === true);
                        lastLoadedSessionId.current = activeChatSessionId;
                    }
                }
            }).catch(err => {
                if (isCurrent) {
                    console.error("Failed to load messages", err);
                    setMessages([], activeChatSessionId);
                    setLinkedChatStreams([]);
                    setComparisonForceWebSearch(false);
                    lastLoadedSessionId.current = activeChatSessionId;
                }
            }).finally(() => {
                if (isCurrent) setIsSessionLoading(false);
            });
        } else if (!activeChatSessionId) {
            setIsSessionLoading(false);
            if (!isLoading && lastLoadedSessionId.current !== null) {
                setLinkedChatStreams([]);
                setComparisonForceWebSearch(false);
                lastLoadedSessionId.current = null;
            }
        }

        return () => {
            isCurrent = false;
        };
    }, [userInfo, activeChatSessionId, chatSessions, canUseChatVariants, getChatMessages, setMessages, isLoading]);

    // Set default persona if none selected OR if current persona is not in the filtered list
    useEffect(() => {
        if (!isWorkspacePresentation && personas.length > 0) {
            const isSelectedInList = selectedPersona && personas.some(p => p.id === selectedPersona.id);
            if (!selectedPersona || !isSelectedInList) {
                // Default to Chip v5 if it's in the allowed list, else pick the first one
                const chip = personas.find(p => p.id === 'p072690a2e4b') || personas[0];
                selectPersona(chip);
            }
        }
    }, [isWorkspacePresentation, personas, selectedPersona, selectPersona]);



    // Focus input immediately on mount
    useEffect(() => {
        focusComposer();
    }, [focusComposer]);

    useEffect(() => {
        return () => {
            ttsAbortControllerRef.current?.abort();
            ttsAbortControllerRef.current = null;

            const { mediaSource, sourceBuffer } = ttsMseRef.current;
            if (mediaSource && sourceBuffer) {
                try {
                    if (mediaSource.readyState === 'open') sourceBuffer.abort();
                } catch { /* ignore */ }
                try {
                    if (mediaSource.readyState === 'open') mediaSource.endOfStream('decode');
                } catch { /* ignore */ }
            }
            ttsMseRef.current = { mediaSource: null, sourceBuffer: null };

            if (audioRef.current) {
                const a = audioRef.current;
                const { onEnded, onError } = ttsAudioListenersRef.current;
                if (onEnded) a.removeEventListener('ended', onEnded);
                if (onError) a.removeEventListener('error', onError);
                ttsAudioListenersRef.current = { onEnded: null, onError: null };
                a.pause();
                a.removeAttribute('src');
                a.load();
            }
            if (currentObjectUrlRef.current) {
                URL.revokeObjectURL(currentObjectUrlRef.current);
                currentObjectUrlRef.current = null;
            }
            if (typeof window !== 'undefined' && window.speechSynthesis) {
                window.speechSynthesis.cancel();
            }
        };
    }, []);

    // Focus input when loading finishes
    useEffect(() => {
        if (!isLoading && !isSessionLoading) {
            focusComposer();
        }
    }, [focusComposer, isLoading, isSessionLoading]);

    // Handle sending a message
    const handleSend = async () => {
        const content = inputValue.trim();
        const shouldAutoSpeak = inputOriginRef.current === 'voice';
        inputOriginRef.current = 'text';
        stopTextToSpeech();
        if (!content || isLoading || !userInfo || !workspacePersonaReady) return;
        reportTeachingInteraction();
        const shouldRunComparisonStream = canUseChatVariants && (useSynapseVariant || comparisonForceWebSearch);
        const comparisonRunId = shouldRunComparisonStream ? crypto.randomUUID() : undefined;

        // Ensure we have a chatId
        let currentChatId = activeChatSessionId;
        if (!currentChatId) {
            currentChatId = addNewChatSession(content);
            if (isWorkspacePresentation && currentChatId) {
                workspaceChatOwnershipRef.current.chatIds.add(currentChatId);
            }
            lastLoadedSessionId.current = currentChatId || null;
            if (currentChatId) {
                navigateToChat(currentChatId, true);
            }
        }
        const existingComparisonStream = linkedChatStreams.find(stream => stream.comparisonStreamId === COMPARISON_STREAM_ID);
        const comparisonStreamChatId = shouldRunComparisonStream
            ? existingComparisonStream?.chatId || getComparisonStreamChatId(currentChatId, COMPARISON_STREAM_ID)
            : undefined;
        const comparisonStream: LinkedChatStream | undefined = comparisonStreamChatId
            ? {
                chatId: comparisonStreamChatId,
                comparisonStreamId: COMPARISON_STREAM_ID,
                label: COMPARISON_STREAM_LABEL,
                settings: {
                    useSynapse: useSynapseVariant,
                    forceWebSearch: comparisonForceWebSearch,
                },
            }
            : undefined;

        if (comparisonStream) {
            setLinkedChatStreams(prev => [
                ...prev.filter(stream => stream.comparisonStreamId !== COMPARISON_STREAM_ID),
                comparisonStream,
            ]);
            setChatSessions((prev: ChatSession[]) => prev.map(session => {
                if (session.chatInfo.chatId !== currentChatId) return session;
                const existing = session.chatInfo.linkedChatStreams || [];
                return {
                    ...session,
                    chatInfo: {
                        ...session.chatInfo,
                        linkedChatStreams: [
                            ...existing.filter(stream => stream.comparisonStreamId !== COMPARISON_STREAM_ID),
                            comparisonStream,
                        ],
                    },
                };
            }));
        }

        const userMessage: GlobalMessage = {
            exchangeId: `user-${Date.now()}`,
            fromUser: true,
            message: content,
            sources: {},
            comparisonRunId,
        };

        setMessages((prev: GlobalMessage[]) => [...prev, userMessage], currentChatId);
        if (comparisonStreamChatId) {
            setMessages((prev: GlobalMessage[]) => [...prev, userMessage], comparisonStreamChatId);
        }
        trackMessageSent(currentChatId!, 'text');
        setInputValue('');
        setIsLoading(true);
        setChatPersistenceStatus((previous) => ({ ...previous, [currentChatId!]: 'sending' }));
        focusComposer();

        const assistantId = `assistant-${crypto.randomUUID()}`;
        const clientTurnId = crypto.randomUUID();
        const variantAssistantId = shouldRunComparisonStream
            ? `variant-${crypto.randomUUID()}`
            : undefined;

        try {
            const userMessageId = userMessage.exchangeId;
            const assistantMessage: GlobalMessage = {
                exchangeId: assistantId,
                fromUser: false,
                message: '',
                sources: {},
                isStreaming: true,
                comparisonRunId,
                useSynapse,
                agenticProgress: 'Request received',
                agenticProgressTrail: ['Request received'],
            } as GlobalMessage & {
                agenticProgress?: string;
                agenticProgressTrail?: string[];
            };

            setMessages((prev: GlobalMessage[]) => [...prev, assistantMessage], currentChatId);

            if (variantAssistantId && comparisonRunId && comparisonStreamChatId) {
                const variantAssistantMessage: GlobalMessage = {
                    exchangeId: variantAssistantId,
                    fromUser: false,
                    message: '',
                    sources: {},
                    isStreaming: true,
                    comparisonRunId,
                    comparisonStreamId: COMPARISON_STREAM_ID,
                    comparisonStreamLabel: COMPARISON_STREAM_LABEL,
                    streamChatId: comparisonStreamChatId,
                    isVariant: true,
                    useSynapse: useSynapseVariant,
                    settings: {
                        useSynapse: useSynapseVariant,
                        forceWebSearch: comparisonForceWebSearch,
                    },
                    agenticProgress: 'Request received',
                    agenticProgressTrail: ['Request received'],
                } as GlobalMessage & {
                    agenticProgress?: string;
                    agenticProgressTrail?: string[];
                };
                setMessages((prev: GlobalMessage[]) => [...prev, variantAssistantMessage], comparisonStreamChatId);
            }

            const persistedConversationHistory = (allMessages[currentChatId] || []).map(msg => ({
                role: msg.fromUser ? 'user' as const : 'assistant' as const,
                content: msg.message,
            }));
            const prependPreambleToHistory = (history: Array<{ role: 'user' | 'assistant'; content: string }>) => {
                const startsWithPreamble = history[0]?.role === 'assistant'
                    && history[0]?.content === openingPreamble;
                return isWorkspacePresentation && openingPreamble && !startsWithPreamble
                    ? [{ role: 'assistant' as const, content: openingPreamble }, ...history]
                    : history;
            };
            const mainConversationHistory = [
                ...prependPreambleToHistory(persistedConversationHistory),
                { role: 'user' as const, content }
            ];
            const comparisonConversationHistory = comparisonStreamChatId
                ? [
                    ...prependPreambleToHistory((allMessages[comparisonStreamChatId] || []).map(msg => ({
                        role: msg.fromUser ? 'user' as const : 'assistant' as const,
                        content: msg.message,
                    }))),
                    { role: 'user' as const, content }
                ]
                : [];

            const runStream = async (params: {
                chatId: string;
                placeholderId: string;
                conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>;
                streamOptions: Parameters<typeof postMessageStream>[6];
                clientTurnId: string;
                attachSafetyToUser: boolean;
                autoSpeak: boolean;
                isVariant?: boolean;
            }) => {
                const streamController = new AbortController();
                const streamTimeoutMs = params.streamOptions?.discoveryMode
                    ? Math.max(CHAT_STREAM_TIMEOUT_MS, 90000)
                    : CHAT_STREAM_TIMEOUT_MS;
                const timeoutId = window.setTimeout(() => {
                    streamController.abort();
                }, streamTimeoutMs);
                let currentAssistantExchangeId = params.placeholderId;
                let latestStreamUpdate: Awaited<ReturnType<typeof consumeChatStream>> | undefined;

                try {
                    const response = await postMessageStream(
                        content,
                        userInfo,
                        params.chatId,
                        selectedPersona,
                        params.conversationHistory,
                        streamController.signal,
                        {
                            ...params.streamOptions,
                            clientTurnId: params.clientTurnId,
                            enableWorkflowProgress: true,
                        } // TODO include useSynapse?
                    );

                    if (!response || !response.ok) {
                        const errorBody = response ? await response.text().catch(() => '') : '';
                        throw new Error(
                            `HTTP error! status: ${response?.status ?? 'no_response'}${errorBody ? ` body: ${errorBody}` : ''}`,
                        );
                    }

                    const finalUpdate = await consumeChatStream(response, (update) => {
                        latestStreamUpdate = update;
                        if (update.id) currentAssistantExchangeId = update.id;
                        setMessages((prev: GlobalMessage[]) =>
                            prev.map(msg => {
                                if (msg.exchangeId === params.placeholderId || msg.exchangeId === update.id) {
                                    const updated = {
                                        ...msg,
                                        message: update.annotatedAnswer || update.accumulatedContent || msg.message,
                                    } as GlobalMessage & {
                                        agenticProgress?: string;
                                        agenticProgressTrail?: string[];
                                        workflowMetadata?: any;
                                        safetyClassification?: any;
                                        retrievalDecision?: any;
                                        conversationMomentum?: any;
                                        alignment?: any;
                                        retrievalSummary?: any;
                                    };
                                    if (update.id) updated.exchangeId = update.id;
                                    if (update.sources) updated.sources = update.sources;
                                    if (update.seeds) updated.seeds = update.seeds;
                                    if (update.retrievedChunkIds) updated.retrievedChunkIds = update.retrievedChunkIds;
                                    if (update.usedChunkIds) updated.usedChunkIds = update.usedChunkIds;
                                    if (update.responseMode) updated.responseMode = update.responseMode;
                                    if (update.workflowMetadata) updated.workflowMetadata = update.workflowMetadata;
                                    if (update.safetyClassification) updated.safetyClassification = update.safetyClassification;
                                    if (update.retrievalDecision) updated.retrievalDecision = update.retrievalDecision;
                                    if (update.conversationMomentum) updated.conversationMomentum = update.conversationMomentum;
                                    if (update.alignment) updated.alignment = update.alignment;
                                    if (update.retrievalSummary) updated.retrievalSummary = update.retrievalSummary;
                                    if (update.toolTrace) updated.toolTrace = update.toolTrace;
                                    if (update.useSynapse !== undefined) updated.useSynapse = update.useSynapse;

                                    if (update.agenticProgress) {
                                        const nextProgressMessage = update.agenticProgress.message;
                                        const existingTrail = (msg as any).agenticProgressTrail || [];

                                        updated.agenticProgress = nextProgressMessage;
                                        updated.agenticProgressTrail =
                                            existingTrail[existingTrail.length - 1] === nextProgressMessage
                                                ? existingTrail
                                                : [...existingTrail, nextProgressMessage];
                                    }
                                    if (update.isComplete) updated.isStreaming = false;
                                    return updated;
                                }

                                if (params.attachSafetyToUser && update.safetyRoute && msg.exchangeId === userMessageId) {
                                    return { ...msg, safetyRoute: update.safetyRoute };
                                }

                                if (params.attachSafetyToUser && update.responseMode && msg.exchangeId === userMessageId) {
                                    return { ...msg, responseMode: update.responseMode };
                                }

                                if (update.useSynapse !== undefined && msg.exchangeId === userMessageId) {
                                    return { ...msg, useSynapse: update.useSynapse };
                                }

                                return msg;
                            }),
                            params.chatId
                        );
                    }, streamController.signal);

                    if (!finalUpdate.annotatedAnswer && !finalUpdate.accumulatedContent) {
                        throw new Error('Empty stream response');
                    }

                    setMessages((prev: GlobalMessage[]) =>
                        prev.map(msg => {
                            if (msg.exchangeId === params.placeholderId || msg.exchangeId === finalUpdate.id || msg.exchangeId === currentAssistantExchangeId) {
                                return {
                                    ...msg,
                                    exchangeId: finalUpdate.id || msg.exchangeId,
                                    message: finalUpdate.annotatedAnswer || finalUpdate.accumulatedContent || msg.message,
                                    sources: finalUpdate.sources || msg.sources,
                                    seeds: finalUpdate.seeds || msg.seeds,
                                    retrievedChunkIds: finalUpdate.retrievedChunkIds || msg.retrievedChunkIds,
                                    usedChunkIds: finalUpdate.usedChunkIds || msg.usedChunkIds,
                                    responseMode: finalUpdate.responseMode ?? msg.responseMode,
                                    toolTrace: finalUpdate.toolTrace ?? msg.toolTrace,
                                    useSynapse: finalUpdate.useSynapse ?? msg.useSynapse,
                                    goal: finalUpdate.goal ?? msg.goal,
                                    isStreaming: false,
                                    workflowMetadata: finalUpdate.workflowMetadata || msg.workflowMetadata,
                                    safetyClassification: finalUpdate.safetyClassification || msg.safetyClassification,
                                    retrievalDecision: finalUpdate.retrievalDecision || msg.retrievalDecision,
                                    conversationMomentum: finalUpdate.conversationMomentum || msg.conversationMomentum,
                                    alignment: finalUpdate.alignment || msg.alignment,
                                    retrievalSummary: finalUpdate.retrievalSummary || msg.retrievalSummary,
                                };
                            }
                            if (params.attachSafetyToUser && finalUpdate.safetyRoute && msg.exchangeId === userMessageId) {
                                return { ...msg, safetyRoute: finalUpdate.safetyRoute };
                            }
                            if (params.attachSafetyToUser && finalUpdate.responseMode && msg.exchangeId === userMessageId) {
                                return { ...msg, responseMode: finalUpdate.responseMode };
                            }
                            if (finalUpdate.useSynapse !== undefined && msg.exchangeId === userMessageId) {
                                return { ...msg, useSynapse: finalUpdate.useSynapse };
                            }
                            return msg;
                        }),
                        params.chatId
                    );

                    if (params.autoSpeak) {
                        const finalText = (finalUpdate.annotatedAnswer || finalUpdate.accumulatedContent || '').trim();
                        const finalId = finalUpdate.id || params.placeholderId;
                        if (finalText) {
                            void speakText(finalText, finalId, currentChatId || undefined);
                        }
                    }
                    return params.streamOptions?.requirePersistenceAck !== true
                        || finalUpdate.persistedChatId === params.chatId;
                } catch (error) {
                    console.error(params.isVariant ? 'Comparison stream chat error:' : 'Chat error:', {
                        error,
                        chatId: params.chatId,
                        personaId: selectedPersona?.id,
                        discoveryMode: params.streamOptions?.discoveryMode === true,
                        streamTimeoutMs,
                        latestStreamUpdate,
                    });
                    void reportError({
                        message: error instanceof Error ? error.message : String(error || 'Chat stream error'),
                        stackTrace: error instanceof Error ? error.stack : undefined,
                        source: 'frontend',
                        severity: 'error',
                        chatId: params.chatId || undefined,
                        exchangeId: latestStreamUpdate?.id || currentAssistantExchangeId || undefined,
                        metadata: {
                            personaId: selectedPersona?.id,
                            isVariant: params.isVariant,
                        },
                    });
                    setMessages((prev: GlobalMessage[]) =>
                        prev.map(msg => {
                            if (msg.exchangeId !== params.placeholderId && msg.exchangeId !== currentAssistantExchangeId) {
                                return msg;
                            }
                            const latestAnswer = (
                                latestStreamUpdate?.annotatedAnswer ||
                                latestStreamUpdate?.accumulatedContent ||
                                msg.message ||
                                ''
                            ).trim();
                            if (latestAnswer) {
                                return {
                                    ...msg,
                                    exchangeId: latestStreamUpdate?.id || msg.exchangeId,
                                    // A server error after content used to arrive as prose and
                                    // sit in the bubble; now it is thrown. Keep what the learner
                                    // already read, but flag it — outside the text, which is
                                    // sent back as history and must match what was persisted.
                                    message: latestAnswer,
                                    interrupted: error instanceof ChatStreamError || undefined,
                                    sources: latestStreamUpdate?.sources || msg.sources,
                                    seeds: latestStreamUpdate?.seeds || msg.seeds,
                                    retrievedChunkIds: latestStreamUpdate?.retrievedChunkIds || msg.retrievedChunkIds,
                                    usedChunkIds: latestStreamUpdate?.usedChunkIds || msg.usedChunkIds,
                                    responseMode: latestStreamUpdate?.responseMode ?? msg.responseMode,
                                    toolTrace: latestStreamUpdate?.toolTrace ?? msg.toolTrace,
                                    useSynapse: latestStreamUpdate?.useSynapse ?? msg.useSynapse,
                                    goal: latestStreamUpdate?.goal ?? msg.goal,
                                    workflowMetadata: latestStreamUpdate?.workflowMetadata || msg.workflowMetadata,
                                    safetyClassification: latestStreamUpdate?.safetyClassification || msg.safetyClassification,
                                    retrievalDecision: latestStreamUpdate?.retrievalDecision || msg.retrievalDecision,
                                    conversationMomentum: latestStreamUpdate?.conversationMomentum || msg.conversationMomentum,
                                    alignment: latestStreamUpdate?.alignment || msg.alignment,
                                    retrievalSummary: latestStreamUpdate?.retrievalSummary || msg.retrievalSummary,
                                    isStreaming: false,
                                };
                            }
                            return {
                                ...msg,
                                message:
                                    error instanceof DOMException && error.name === 'AbortError'
                                        ? 'The response took too long. Please retry.'
                                        : params.isVariant
                                            ? 'Comparison stream failed. Please retry.'
                                            : params.streamOptions?.discoveryMode
                                                ? `Scout failed locally: ${describeChatError(error)}`
                                                : 'Sorry, something went wrong. Please try again.',
                                sources: {},
                                isStreaming: false,
                            };
                        }),
                        params.chatId
                    );
                    return false;
                } finally {
                    window.clearTimeout(timeoutId);
                }
            };

            const mainStreamTask = runStream({
                placeholderId: assistantId,
                chatId: currentChatId,
                conversationHistory: mainConversationHistory,
                clientTurnId,
                streamOptions: {
                    // Learning workspaces identify and link the chat as soon as this
                    // stream finishes. Ordinary chat can persist in the background;
                    // the video handoff has a separate preparation step before joining.
                    ...(isWorkspacePresentation && lockedPersona ? { requirePersistenceAck: true } : {}),
                    forceWebSearch: isScoutMode ? false : forceWebSearch,
                    useSynapse: isScoutMode ? true : useSynapse,
                    discoveryMode: isScoutMode || undefined,
                    discoveryExtended: isScoutMode ? scoutExtended : undefined,
                    discoveryGuideProfileId: isScoutMode ? scoutGuideProfile.id : undefined,
                    discoveryTestRunId: activeDiscoveryTestRunId,
                    synapseWriteMode: isScoutMode ? 'sync' as const : undefined,
                    comparisonRunId,
                    chapterSlug,
                    requireChapterScope: isWorkspacePresentation || undefined,
                    // The server collapses a spoken utterance that arrives as
                    // several requests only for voice turns; tell it which this is.
                    learnerInputMode: shouldAutoSpeak ? 'voice' as const : 'text' as const,
                },
                attachSafetyToUser: true,
                autoSpeak: shouldAutoSpeak,
            });
            const comparisonStreamTasks: Array<Promise<boolean>> = [];

            if (variantAssistantId && comparisonRunId) {
                comparisonStreamTasks.push(runStream({
                    placeholderId: variantAssistantId,
                    chatId: comparisonStreamChatId!,
                    conversationHistory: comparisonConversationHistory,
                    clientTurnId: `${clientTurnId}:variant`,
                    streamOptions: {
                        forceWebSearch: comparisonForceWebSearch,
                        useSynapse: useSynapseVariant,
                        comparisonRunId,
                        comparisonStreamId: COMPARISON_STREAM_ID,
                        comparisonStreamLabel: COMPARISON_STREAM_LABEL,
                        discoveryTestRunId: activeDiscoveryTestRunId,
                        parentChatId: currentChatId,
                        comparisonSettings: {
                            useSynapse: useSynapseVariant,
                            forceWebSearch: comparisonForceWebSearch,
                        },
                        chapterSlug,
                        requireChapterScope: isWorkspacePresentation || undefined,
                    },
                    attachSafetyToUser: false,
                    autoSpeak: false,
                    isVariant: true,
                }));
            }

            const mainStreamPersisted = await mainStreamTask;
            setChatPersistenceStatus((previous) => ({
                ...previous,
                [currentChatId!]: mainStreamPersisted ? 'persisted' : 'failed',
            }));
            if (!mainStreamPersisted) {
                // Preserve a direct retry path instead of leaving the user with a
                // disabled video handoff and an empty composer after a transient
                // persistence/stream failure.
                if (activeChatSessionIdRef.current === currentChatId) {
                    setInputValue((current) => current || content);
                }
            }
            if (
                mainStreamPersisted &&
                onChatSessionIdentified &&
                !identifiedChatIdsRef.current.has(currentChatId) &&
                !linkingChatIdsRef.current.has(currentChatId)
            ) {
                const persistedChatId = currentChatId;
                linkingChatIdsRef.current.add(persistedChatId);
                try {
                    void Promise.resolve(onChatSessionIdentified(persistedChatId))
                        .then((linked) => {
                            if (linked !== false) identifiedChatIdsRef.current.add(persistedChatId);
                        })
                        .catch((error) => {
                            console.error('[text-chat] Failed to link persisted workspace chat', {
                                error,
                                chatId: persistedChatId,
                            });
                        })
                        .finally(() => {
                            linkingChatIdsRef.current.delete(persistedChatId);
                        });
                } catch (error) {
                    linkingChatIdsRef.current.delete(persistedChatId);
                    console.error('[text-chat] Failed to link persisted workspace chat', { error, chatId: persistedChatId });
                }
            }
            await Promise.all(comparisonStreamTasks);
        } catch (error) {
            setChatPersistenceStatus((previous) => ({ ...previous, [currentChatId!]: 'failed' }));
            if (activeChatSessionIdRef.current === currentChatId) {
                setInputValue((current) => current || content);
            }
            console.error('Chat error:', {
                error,
                personaId: selectedPersona?.id,
                isScoutMode,
            });
            void reportError({
                message: error instanceof Error ? error.message : String(error || 'Chat streaming error'),
                stackTrace: error instanceof Error ? error.stack : undefined,
                source: 'frontend',
                severity: 'error',
                chatId: currentChatId || undefined,
                exchangeId: assistantId || undefined,
                metadata: {
                    personaId: selectedPersona?.id,
                    isScoutMode,
                },
            });
            setMessages((prev: GlobalMessage[]) => [
                ...prev.filter(msg => msg.exchangeId !== assistantId),
                {
                    exchangeId: `error-${Date.now()}`,
                    fromUser: false,
                    message:
                        error instanceof DOMException && error.name === 'AbortError'
                            ? 'The response took too long. Please retry.'
                            : isScoutMode
                                ? `Scout failed before streaming: ${describeChatError(error)}`
                                : 'Sorry, something went wrong. Please try again.',
                    sources: {}
                },
            ], currentChatId);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle feedback submission
    const handleFeedback = async (
      exchangeId: string,
      kind: 'up' | 'down' | null,
      comment?: string,
      chatIdOverride?: string,
      categories?: FeedbackCategoryId[],
    ) => {
        if (!userInfo || !activeChatSessionId) return;
        const feedbackChatId = chatIdOverride || activeChatSessionId;
        const rating = kind === 'up' ? 1 : (kind === 'down' ? -1 : null);
        try {
            await putRating(feedbackChatId, exchangeId, rating, comment, userInfo.accessToken, selectedPersona?.id, categories);
            setMessages((prev: GlobalMessage[]) => prev.map(msg =>
                msg.exchangeId === exchangeId
                    ? { ...msg, rating: rating ?? undefined, comment, feedbackCategory: categories ?? [] }
                    : msg
            ), feedbackChatId);
        } catch (error) {
            console.error("Failed to submit feedback:", error);
        }
    };

    // Handle keyboard events
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const startVoiceRecognition = () => {
        if (isLoading) return;

        const SpeechRecognitionCtor = window.SpeechRecognition || window.webkitSpeechRecognition;

        if (!SpeechRecognitionCtor) {
            setVoiceError('Speech recognition is not supported in this browser.');
            return;
        }

        setVoiceError('');
        finalTranscriptRef.current = '';

        if (recognitionRef.current) {
            recognitionRef.current.abort();
            recognitionRef.current = null;
        }

        const recognition = new SpeechRecognitionCtor();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        recognition.onstart = () => setIsRecording(true);

        recognition.onresult = (event: SpeechRecognitionEventLike) => {
            inputOriginRef.current = 'voice';
            let interimTranscript = '';
            let finalTranscriptChunk = '';

            for (let i = event.resultIndex; i < event.results.length; i += 1) {
                const result = event.results[i];
                const transcript = result[0]?.transcript || '';
                if (result.isFinal) {
                    finalTranscriptChunk += transcript;
                } else {
                    interimTranscript += transcript;
                }
            }

            if (finalTranscriptChunk) {
                finalTranscriptRef.current = `${finalTranscriptRef.current} ${finalTranscriptChunk}`.trim();
            }

            const combinedTranscript = `${finalTranscriptRef.current} ${interimTranscript}`.trim();
            setInputValue(combinedTranscript);
        };

        recognition.onerror = (event: SpeechRecognitionErrorEventLike) => {
            console.error('Speech recognition error:', event.error);
            setIsRecording(false);
            if (event.error === 'not-allowed') {
                setVoiceError('Microphone permission was denied.');
            } else if (event.error === 'no-speech') {
                setVoiceError('No speech detected. Please try again.');
            } else {
                setVoiceError('Voice input failed. Please try again.');
            }
        };

        recognition.onend = () => {
            setIsRecording(false);
            recognitionRef.current = null;
            finalTranscriptRef.current = '';
            focusComposer();
        };

        recognitionRef.current = recognition;
        recognition.start();
    };

    const stopVoiceRecognition = () => {
        if (recognitionRef.current) recognitionRef.current.stop();
    };

    const handleVoiceButtonClick = () => {
        if (isRecording) stopVoiceRecognition();
        else startVoiceRecognition();
    };

    const stopTextToSpeech = () => {
        if (typeof window === 'undefined') return;

        ttsAbortControllerRef.current?.abort();
        ttsAbortControllerRef.current = null;

        const { mediaSource, sourceBuffer } = ttsMseRef.current;
        if (mediaSource && sourceBuffer) {
            try {
                if (mediaSource.readyState === 'open') sourceBuffer.abort();
            } catch { /* ignore */ }
            try {
                if (mediaSource.readyState === 'open') mediaSource.endOfStream('decode');
            } catch { /* ignore */ }
        }
        ttsMseRef.current = { mediaSource: null, sourceBuffer: null };

        if (audioRef.current) {
            const a = audioRef.current;
            const { onEnded, onError } = ttsAudioListenersRef.current;
            if (onEnded) a.removeEventListener('ended', onEnded);
            if (onError) a.removeEventListener('error', onError);
            ttsAudioListenersRef.current = { onEnded: null, onError: null };
            a.pause();
            a.removeAttribute('src');
            a.load();
        }
        if (currentObjectUrlRef.current) {
            URL.revokeObjectURL(currentObjectUrlRef.current);
            currentObjectUrlRef.current = null;
        }

        if (window.speechSynthesis) window.speechSynthesis.cancel();
        speechUtteranceRef.current = null;
        setCurrentlyPlayingId(null);
    };

    const speakWithBrowserTts = (cleanedText: string, messageId?: string) => {
        if (typeof window === 'undefined' || !window.speechSynthesis) return;

        const utterance = new SpeechSynthesisUtterance(cleanedText);
        utterance.rate = 1;
        utterance.pitch = 1;
        utterance.volume = 1;

        utterance.onend = () => {
            speechUtteranceRef.current = null;
            setCurrentlyPlayingId(null);
        };
        utterance.onerror = () => {
            speechUtteranceRef.current = null;
            setCurrentlyPlayingId(null);
        };

        speechUtteranceRef.current = utterance;
        window.speechSynthesis.speak(utterance);
    };

    const speakText = async (text: string, exchangeId?: string, chatId?: string) => {
        if (typeof window === 'undefined') return;
        if (!isTtsEnabled) return;

        const cleanedText = stripCitationsForSpeech(text);
        if (!cleanedText) return;

        /** Matches `localMessages` id for assistant rows: `${exchangeId}-assistant` */
        const playbackId = exchangeId ? `${exchangeId}-assistant` : undefined;

        stopTextToSpeech();
        if (playbackId) {
            setCurrentlyPlayingId(playbackId);
        }

        ttsAbortControllerRef.current = new AbortController();
        const { signal } = ttsAbortControllerRef.current;

        const personaId = selectedPersona?.id;
        const token = userInfo?.accessToken;

        if (!exchangeId || !chatId) {
            console.warn('[text-chat] TTS: missing chatId or exchangeId; using browser speech (no persistence)', {
                chatId,
                exchangeId,
            });
            speakWithBrowserTts(cleanedText, playbackId);
            return;
        }

        const ttsContext = { chatId, exchangeId };
        if (!personaId || !token) {
            speakWithBrowserTts(cleanedText, playbackId);
            return;
        }

        const playBlobTts = async () => {
            const blob = await postTts(cleanedText, personaId, token, ttsContext, signal);
            if (signal.aborted) {
                return;
            }
            if (blob.size === 0) {
                speakWithBrowserTts(cleanedText, playbackId);
                return;
            }

            const url = URL.createObjectURL(blob);
            currentObjectUrlRef.current = url;

            let audio = audioRef.current;
            if (!audio) {
                audio = new Audio();
                audioRef.current = audio;
            }

            const onDone = () => {
                audio!.removeEventListener('ended', onDone);
                audio!.removeEventListener('error', onErr);
                ttsAudioListenersRef.current = { onEnded: null, onError: null };
                if (currentObjectUrlRef.current === url) {
                    URL.revokeObjectURL(url);
                    currentObjectUrlRef.current = null;
                }
                if (audioRef.current?.src === url) {
                    audioRef.current.removeAttribute('src');
                    audioRef.current.load();
                }
                setCurrentlyPlayingId(null);
            };

            const onErr = () => {
                audio!.removeEventListener('ended', onDone);
                audio!.removeEventListener('error', onErr);
                ttsAudioListenersRef.current = { onEnded: null, onError: null };
                if (currentObjectUrlRef.current === url) {
                    URL.revokeObjectURL(url);
                    currentObjectUrlRef.current = null;
                }
                setCurrentlyPlayingId(null);
                speakWithBrowserTts(cleanedText, playbackId);
            };

            ttsAudioListenersRef.current = { onEnded: onDone, onError: onErr };
            audio.src = url;
            audio.addEventListener('ended', onDone);
            audio.addEventListener('error', onErr);

            try {
                await audio.play();
            } catch {
                audio.removeEventListener('ended', onDone);
                audio.removeEventListener('error', onErr);
                ttsAudioListenersRef.current = { onEnded: null, onError: null };
                if (currentObjectUrlRef.current === url) {
                    URL.revokeObjectURL(url);
                    currentObjectUrlRef.current = null;
                }
                speakWithBrowserTts(cleanedText, playbackId);
            }
        };

        try {
            const useMse =
                typeof window.MediaSource !== 'undefined' &&
                MediaSource.isTypeSupported('audio/mpeg');

            if (useMse) {
                try {
                    const response = await postTtsStream(cleanedText, personaId, token, ttsContext, signal);
                    if (signal.aborted) {
                        return;
                    }
                    if (!response.body) {
                        await playBlobTts();
                        return;
                    }

                    const reader = response.body.getReader();
                    const mediaSource = new MediaSource();
                    const objectUrl = URL.createObjectURL(mediaSource);
                    currentObjectUrlRef.current = objectUrl;
                    ttsMseRef.current = { mediaSource, sourceBuffer: null };

                    let audio = audioRef.current;
                    if (!audio) {
                        audio = new Audio();
                        audioRef.current = audio;
                    }

                    const onDone = () => {
                        audio!.removeEventListener('ended', onDone);
                        audio!.removeEventListener('error', onErr);
                        ttsAudioListenersRef.current = { onEnded: null, onError: null };
                        ttsMseRef.current = { mediaSource: null, sourceBuffer: null };
                        if (currentObjectUrlRef.current === objectUrl) {
                            URL.revokeObjectURL(objectUrl);
                            currentObjectUrlRef.current = null;
                        }
                        if (audioRef.current?.src === objectUrl) {
                            audioRef.current.removeAttribute('src');
                            audioRef.current.load();
                        }
                        setCurrentlyPlayingId(null);
                    };

                    const onErr = () => {
                        audio!.removeEventListener('ended', onDone);
                        audio!.removeEventListener('error', onErr);
                        ttsAudioListenersRef.current = { onEnded: null, onError: null };
                        ttsMseRef.current = { mediaSource: null, sourceBuffer: null };
                        if (currentObjectUrlRef.current === objectUrl) {
                            URL.revokeObjectURL(objectUrl);
                            currentObjectUrlRef.current = null;
                        }
                        if (audioRef.current?.src === objectUrl) {
                            audioRef.current.removeAttribute('src');
                            audioRef.current.load();
                        }
                        setCurrentlyPlayingId(null);
                        speakWithBrowserTts(cleanedText, playbackId);
                    };

                    ttsAudioListenersRef.current = { onEnded: onDone, onError: onErr };
                    audio.addEventListener('ended', onDone);
                    audio.addEventListener('error', onErr);

                    const sourceReady = new Promise<void>((resolve, reject) => {
                        const onOpen = () => {
                            try {
                                const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
                                ttsMseRef.current = { mediaSource, sourceBuffer };
                                void appendMpegChunksToSourceBuffer(reader, mediaSource, sourceBuffer, signal)
                                    .catch((e) => {
                                        if (!signal.aborted) {
                                            console.error('[tts] MSE append failed', e);
                                            onErr();
                                        }
                                    });
                                resolve();
                            } catch (e) {
                                reject(e);
                            }
                        };
                        mediaSource.addEventListener('sourceopen', onOpen, { once: true });
                    });

                    audio.src = objectUrl;
                    await sourceReady;

                    try {
                        await audio.play();
                    } catch {
                        audio.removeEventListener('ended', onDone);
                        audio.removeEventListener('error', onErr);
                        ttsAudioListenersRef.current = { onEnded: null, onError: null };
                        ttsMseRef.current = { mediaSource: null, sourceBuffer: null };
                        if (currentObjectUrlRef.current === objectUrl) {
                            URL.revokeObjectURL(objectUrl);
                            currentObjectUrlRef.current = null;
                        }
                        speakWithBrowserTts(cleanedText, playbackId);
                    }
                } catch {
                    if (!signal.aborted) {
                        try {
                            await playBlobTts();
                        } catch {
                            speakWithBrowserTts(cleanedText, playbackId);
                        }
                    }
                }
            } else {
                await playBlobTts();
            }
        } catch {
            if (!signal.aborted) { 
                speakWithBrowserTts(cleanedText, playbackId); 
            }
        }
    };

    const returnText = async (text: string, messageId?: string): Promise<HTMLAudioElement | undefined> => {
        const cleanedText = stripCitationsForSpeech(text);
        if (!cleanedText) return undefined;
    
        const personaId = selectedPersona?.id;
        const token = userInfo?.accessToken;
        if (!personaId || !token) return undefined;
    
        try {
            const signal = new AbortController().signal;
            const response = await postTtsStream(cleanedText, personaId, token, undefined, signal);
            const reader = response.body!.getReader();
            const mediaSource = new MediaSource();
            const objectUrl = URL.createObjectURL(mediaSource);
            const audio = new Audio();
    
            // ← must set src BEFORE awaiting sourceopen, otherwise sourceopen never fires
            audio.src = objectUrl;
    
            await new Promise<void>((resolve, reject) => {
                mediaSource.addEventListener('sourceopen', () => {
                    try {
                        const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
                        void appendMpegChunksToSourceBuffer(reader, mediaSource, sourceBuffer, signal)
                            .catch(reject);
                        resolve();
                    } catch (e) {
                        reject(e);
                    }
                }, { once: true });
            });
    
            audio.addEventListener('ended', () => URL.revokeObjectURL(objectUrl), { once: true });
            audio.addEventListener('error', () => URL.revokeObjectURL(objectUrl), { once: true });
    
            return audio;
        } catch (err: any) {
            const messageMatch = err?.message?.match(/TTS failed \((\d+)\)/);
            const status = messageMatch ? parseInt(messageMatch[1], 10) : undefined;
            if (status === 429) {
                console.warn('[returnText] TTS rate limited — try again in a moment.');
            } else {
                console.error('[returnText] TTS request failed:', err);
            }
            return undefined;
        }
    };

    const context = useContext(profileContext);
    if (!context) return null;
    const { setActivateProfile } = context;

    const commonPaneProps = {
        showClassification,
        showWorkflowSteps,
        userEmail: userInfo?.email,
        onFeedback: handleFeedback,
        onSpeak: speakText,
        onStopSpeak: stopTextToSpeech,
        currentlyPlayingId,
        returnSpeak: returnText,
        persona: selectedPersona,
    };

    return (
        <div className={`text-chat-page${isWorkspacePresentation ? ' text-chat-page--workspace' : ''}`}>
            <div className="chat-container">
                <header className="chat-header" ref={(el) => { chatHeaderRef.current = el; }}>
                    {!isWorkspacePresentation && <button
                        type="button"
                        className="companion-trigger"
                        disabled={personas.length <= 1 && !personaLoadError}
                        style={{ cursor: personas.length <= 1 && !personaLoadError ? 'default' : 'pointer' }}
                        onClick={(e) => {
                            if (personas.length <= 1) {
                                if (personaLoadError) loadPersonas();
                                return;
                            }
                            const trigger = e.currentTarget;
                            // Auto-expand the group containing the active persona if it's not the primary
                            const groups = groupPersonasByBaseName(personas);
                            const activeGroup = groups.find((g) =>
                                g.variants.some((v) => v.id === selectedPersona?.id),
                            );
                            if (activeGroup && activeGroup.primary.id !== selectedPersona?.id) {
                                setExpandedCompanionGroups((prev) => {
                                    const next = new Set(prev);
                                    next.add(activeGroup.baseName);
                                    return next;
                                });
                            }
                            setCompanionMenuAnchorEl(trigger);
                        }}
                        aria-haspopup="listbox"
                        aria-expanded={Boolean(companionMenuAnchorEl)}
                        aria-label={selectedPersona?.name ? `Switch from ${selectedPersona.name}` : 'Choose companion'}
                    >
                        <Avatar
                            src={selectedPersona?.imageURL}
                            alt={selectedPersona?.name || 'Companion'}
                            className="companion-trigger-avatar"
                        >
                            {selectedPersona?.name?.charAt(0) || '?'}
                        </Avatar>
                        <div className="companion-trigger-text">
                            <span className="companion-trigger-name">
                                {getPersonaDisplayName(selectedPersona) || 'Choose a companion'}
                            </span>
                            {personaLoadError && personas.length === 0 ? (
                                <span className="companion-trigger-hint" style={{ color: '#d32f2f' }}>Failed to load • Tap to retry</span>
                            ) : personas.length > 1 ? (
                                <span className="companion-trigger-hint">Tap to switch</span>
                            ) : null}
                        </div>
                        {personas.length > 1 && (
                            <KeyboardArrowDownRoundedIcon className="companion-trigger-chevron" />
                        )}
                    </button>}

                    <div className="chat-header-actions">
                        {(isWorkspacePresentation
                            ? allowWorkspaceNewChat
                            : (config.showNewChatButton ?? true)) && <Tooltip title="New chat">
                            <button
                                type="button"
                                className="new-chat-button"
                                onClick={() => {
                                    if (companionMenuAnchorEl) setCompanionMenuAnchorEl(null);
                                    if (chatToolsAnchorEl) setChatToolsAnchorEl(null);
                                    if (isWorkspacePresentation) onWorkspaceNewChat?.();
                                    resetSelectedChatSession();
                                    setLinkedChatStreams([]);
                                    setInputValue('');
                                    setAutoStartTargetPersona(null);
                                    discoveryAutoStartAttemptRef.current = null;
                                    lastLoadedSessionId.current = null;
                                    navigateToChat();
                                    focusComposer();
                                }}
                                aria-label="New chat"
                            >
                                <MapsUgcRoundedIcon className="new-chat-button-icon" />
                            </button>
                        </Tooltip>}
                        {!isWorkspacePresentation && canAccessCall(userInfo?.groups || []) && !isScoutMode && (
                            <Tooltip title={
                                typeof activeChatSessionId === 'string' && chatPersistenceStatus[activeChatSessionId] === 'failed'
                                    ? 'The latest response was not saved. Retry it before starting a video call.'
                                    : 'Start a video call with this companion'
                            }>
                                <button
                                    type="button"
                                    className="video-call-button"
                                    onClick={() => {
                                        if (!selectedPersona) return;
                                        if (companionMenuAnchorEl) setCompanionMenuAnchorEl(null);
                                        setVideoCallOpen(true);
                                    }}
                                    disabled={
                                        !selectedPersona ||
                                        (typeof activeChatSessionId === 'string' &&
                                            (chatPersistenceStatus[activeChatSessionId] === 'sending' ||
                                                chatPersistenceStatus[activeChatSessionId] === 'failed'))
                                    }
                                    aria-label="Start video call"
                                >
                                    <VideocamRoundedIcon className="video-call-button-icon" />
                                    <span className="video-call-button-label">Video call</span>
                                </button>
                            </Tooltip>
                        )}
                    </div>

                    {!isWorkspacePresentation && <Popover
                        open={Boolean(companionMenuAnchorEl)}
                        anchorEl={companionMenuAnchorEl}
                        onClose={() => setCompanionMenuAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        PaperProps={{ className: 'companion-menu-paper' }}
                        disableScrollLock
                    >
                        <div className="companion-menu">
                            <div className="companion-menu-header">Switch companion</div>
                            <ul className="companion-menu-list" role="listbox">
                                {groupPersonasByBaseName(personas).map((group) => {
                                    const hasOlderVersions = group.variants.length > 1;
                                    const isExpanded = expandedCompanionGroups.has(group.baseName);
                                    const primaryActive = selectedPersona?.id === group.primary.id;
                                    const groupContainsActive = group.variants.some(
                                        (v) => v.id === selectedPersona?.id,
                                    );
                                    const primaryVersion = parsePersonaVariantName(group.primary.name).version;
                                    const olderVariants = group.variants.slice(1);

                                    return (
                                        <li key={group.baseName} className="companion-menu-group">
                                            <div className="companion-menu-row">
                                                <button
                                                    type="button"
                                                    role="option"
                                                    aria-selected={primaryActive}
                                                    className={`companion-menu-item${primaryActive ? ' is-active' : ''}`}
                                                    onClick={() => {
                                                        if (!primaryActive) selectPersona(group.primary);
                                                        setCompanionMenuAnchorEl(null);
                                                    }}
                                                >
                                                    <Avatar
                                                        src={group.primary.imageURL}
                                                        alt={group.baseName}
                                                        className="companion-menu-avatar"
                                                    >
                                                        {group.baseName.charAt(0) || '?'}
                                                    </Avatar>
                                                    <span className="companion-menu-name">
                                                        {getPersonaDisplayName(group.primary, group.baseName)}
                                                    </span>
                                                    {primaryVersion != null && (
                                                        <span className="companion-menu-version">v{primaryVersion}</span>
                                                    )}
                                                    {primaryActive && (
                                                        <CheckRoundedIcon className="companion-menu-check" />
                                                    )}
                                                </button>
                                                {hasOlderVersions && (
                                                    <button
                                                        type="button"
                                                        className={`companion-menu-expand${isExpanded ? ' is-expanded' : ''}`}
                                                        aria-expanded={isExpanded}
                                                        aria-label={isExpanded
                                                            ? `Hide older ${group.baseName} versions`
                                                            : `Show older ${group.baseName} versions`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setExpandedCompanionGroups((prev) => {
                                                                const next = new Set(prev);
                                                                if (next.has(group.baseName)) {
                                                                    next.delete(group.baseName);
                                                                } else {
                                                                    next.add(group.baseName);
                                                                }
                                                                return next;
                                                            });
                                                        }}
                                                    >
                                                        <KeyboardArrowDownRoundedIcon
                                                            className="companion-menu-expand-icon"
                                                        />
                                                    </button>
                                                )}
                                            </div>
                                            {hasOlderVersions && isExpanded && (
                                                <ul className="companion-menu-variants" role="group">
                                                    {olderVariants.map((variant) => {
                                                        const variantActive = selectedPersona?.id === variant.id;
                                                        const variantVersion = parsePersonaVariantName(variant.name).version;
                                                        return (
                                                            <li key={variant.id}>
                                                                <button
                                                                    type="button"
                                                                    role="option"
                                                                    aria-selected={variantActive}
                                                                    className={`companion-menu-variant${variantActive ? ' is-active' : ''}`}
                                                                    onClick={() => {
                                                                        if (!variantActive) selectPersona(variant);
                                                                        setCompanionMenuAnchorEl(null);
                                                                    }}
                                                                >
                                                                    <span className="companion-menu-variant-label">
                                                                        {getPersonaDisplayName(
                                                                            variant,
                                                                            variantVersion != null
                                                                                ? `Version ${variantVersion}`
                                                                                : variant.name
                                                                        )}
                                                                    </span>
                                                                    {variantActive && (
                                                                        <CheckRoundedIcon className="companion-menu-check" />
                                                                    )}
                                                                </button>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            )}
                                            {hasOlderVersions && !isExpanded && groupContainsActive && !primaryActive && (
                                                <div className="companion-menu-active-hint">
                                                    Using {parsePersonaVariantName(selectedPersona?.name).version != null
                                                        ? `version ${parsePersonaVariantName(selectedPersona?.name).version}`
                                                        : selectedPersona?.name}
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </Popover>}
                </header>

                {personaLoadError && personas.length === 0 && !isWorkspacePresentation && (
                    <div
                        role="alert"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 14px',
                            marginBottom: '8px',
                            borderRadius: '8px',
                            background: 'rgba(211, 47, 47, 0.08)',
                            border: '1px solid rgba(211, 47, 47, 0.25)',
                            color: 'var(--color-text)',
                            fontSize: '0.85rem',
                        }}
                    >
                        <span>Unable to load companions. Please check your connection or refresh the page.</span>
                        <button
                            type="button"
                            onClick={() => loadPersonas()}
                            style={{
                                background: 'transparent',
                                border: '1px solid currentColor',
                                borderRadius: '4px',
                                padding: '2px 8px',
                                cursor: 'pointer',
                                color: 'inherit',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                marginLeft: '12px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Retry
                        </button>
                    </div>
                )}

                <AdminGoalChecklistBar
                    groups={userInfo?.groups}
                    liveSnapshot={goalChecklistSnapshot}
                    defaultExpanded={!isMobile}
                    visible={showGoalChecklist}
                />
                {isScoutMode && scoutDiscoveryDone && !scoutExtended && mainPaneMessages.length > 0 && (
                    <div className="scout-complete-card" role="group" aria-label="Discovery complete options">
                        <div className="scout-complete-copy">
                            {autoStartTargetPersona
                                ? `${scoutGuideProfile.displayName} has enough context for now. You can return to ${autoStartTargetPersona.name} and keep going with personalization on.`
                                : scoutGuideProfile.completionCopy}
                        </div>
                        <div className="scout-complete-actions">
                            <button
                                type="button"
                                className="scout-complete-button scout-complete-primary"
                                onClick={(event) => {
                                    if (autoStartTargetPersona) {
                                        selectPersona(autoStartTargetPersona);
                                        return;
                                    }
                                    setCompanionMenuAnchorEl(event.currentTarget);
                                }}
                            >
                                {autoStartTargetPersona ? `Return to ${autoStartTargetPersona.name}` : 'Choose your Avatar'}
                            </button>
                            <button
                                type="button"
                                className="scout-complete-button"
                                onClick={chooseScoutExtended}
                            >
                                {scoutGuideProfile.continueLabel}
                            </button>
                        </div>
                    </div>
                )}
                <div
                    className={`messages-container${isSplitChatActive ? ' split-chat-active' : ''}`}
                    onWheel={() => reportTeachingInteraction()}
                    onTouchMove={() => reportTeachingInteraction()}
                    onPointerDown={() => reportTeachingInteraction()}
                    onKeyDown={handleMessagesKeyDown}
                    tabIndex={isWorkspacePresentation ? 0 : undefined}
                >
                    {isSessionLoading ? (
                        <div className="empty-state">
                            <CircularProgress size={40} />
                            <p>Loading conversation...</p>
                        </div>
                    ) : !isWorkspacePresentation && pendingDiscoveryGuidePersona && pendingDiscoveryGuideProfile && mainPaneMessages.length === 0 ? (
                        <div className="scout-start-state">
                            <Avatar
                                className="scout-empty-avatar"
                                src={pendingDiscoveryGuidePersona.imageURL || SCOUT_AVATAR_IMAGE_URL}
                                alt={pendingDiscoveryGuideProfile.displayName}
                            >
                                {pendingDiscoveryGuideProfile.fallbackAvatarText}
                            </Avatar>
                            <div className="scout-start-card" role="group" aria-label="Discovery start options">
                                <div className="scout-empty-title">
                                    Start with {pendingDiscoveryGuideProfile.displayName}
                                </div>
                                <div className="scout-empty-copy">
                                    {pendingDiscoveryGuideProfile.displayName} can learn a little context first so future conversations with {autoStartTargetPersona?.name || selectedPersona?.name || 'your Avatar'} fit better.
                                </div>
                                <div className="scout-start-actions">
                                    <button
                                        type="button"
                                        className="scout-complete-button scout-complete-primary"
                                        onClick={() => selectPersona(pendingDiscoveryGuidePersona)}
                                    >
                                        Start a chat with {pendingDiscoveryGuideProfile.displayName}
                                    </button>
                                    <button
                                        type="button"
                                        className="scout-complete-button"
                                        onClick={() => {
                                            setPendingDiscoveryGuidePersona(null);
                                            setAutoStartTargetPersona(null);
                                            setAutoStartDiscoveryStatus('skipped');
                                            focusComposer();
                                        }}
                                    >
                                        Continue to {autoStartTargetPersona?.name || selectedPersona?.name || 'Avatar'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : isScoutMode && mainPaneMessages.length === 0 ? (
                        <div className="scout-empty-state">
                            <Avatar
                                className="scout-empty-avatar"
                                src={selectedPersona?.imageURL || SCOUT_AVATAR_IMAGE_URL}
                                alt={scoutGuideProfile.displayName}
                            >
                                {scoutGuideProfile.fallbackAvatarText}
                            </Avatar>
                            <div className="scout-empty-dialogue">
                                {!showScoutStarterQuestion ? (
                                    <div className="scout-empty-bubble">
                                        <div className="scout-empty-title">
                                            {scoutReturningUser
                                                ? scoutGuideProfile.returningTitle
                                                : scoutGuideProfile.openingTitle}
                                        </div>
                                        <div className="scout-empty-copy">
                                            {scoutReturningUser
                                                ? scoutGuideProfile.returningCopy
                                                : scoutGuideProfile.openingCopy}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="scout-empty-bubble scout-empty-bubble-delayed">
                                        <div className="scout-empty-title">{scoutGuideProfile.starterTitle}</div>
                                        <div className="scout-empty-copy">
                                            {scoutGuideProfile.starterPrompt}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : isSplitChatActive ? (
                        <div className="split-chat-grid">
                            <ChatPane
                                {...commonPaneProps}
                                className="main-chat-pane"
                                messages={chatPaneMessages}
                                isLoading={isLoading}
                                header="Main chat"
                                chatId={activeChatSessionId || undefined}
                            />
                            <ChatPane
                                {...commonPaneProps}
                                className="comparison-chat-pane"
                                messages={comparisonChatPaneMessages}
                                isLoading={isLoading}
                                header="Comparison chat"
                                chatId={comparisonPaneMessages[0]?.streamChatId || activeChatSessionId || undefined}
                            />
                        </div>
                    ) : (
                        <ChatPane
                            {...commonPaneProps}
                            messages={chatPaneMessages}
                            isLoading={isLoading}
                            useWatermark
                            chatId={activeChatSessionId || undefined}
                        />
                    )}
                </div>

                <div className="input-container" ref={inputContainerRef}>
                    <div className="input-wrapper">
                        {!hideChatTools && (
                            <Tooltip title="Tools">
                                <IconButton
                                    size="small"
                                    className="chat-tools-trigger"
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                                        setChatToolsAnchorEl(e.currentTarget)
                                    }
                                    aria-label="Open chat tools"
                                    disabled={isLoading}
                                >
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {!hideChatTools && (
                            <Popover
                                open={Boolean(chatToolsAnchorEl)}
                                anchorEl={chatToolsAnchorEl}
                                onClose={() => setChatToolsAnchorEl(null)}
                                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                disableScrollLock
                            >
                                <Box sx={{ px: 2, py: 1.5, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <MenuItem
                                    disableGutters
                                    onClick={() => {
                                        setChatToolsAnchorEl(null);
                                        setActivateProfile(true);
                                    }}
                                    sx={{
                                        justifyContent: 'flex-start',
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                        <Settings fontSize="small" />
                                    </ListItemIcon>
                                    Settings & Profile
                                </MenuItem>
                                <MenuItem
                                    disableGutters
                                    onClick={() => {
                                        toggleDarkMode();
                                        setChatToolsAnchorEl(null);
                                    }}
                                    sx={{
                                        justifyContent: 'flex-start',
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                        {isDarkMode
                                            ? <LightModeRoundedIcon fontSize="small" />
                                            : <DarkModeRoundedIcon fontSize="small" />}
                                    </ListItemIcon>
                                    {isDarkMode ? 'Light mode' : 'Dark mode'}
                                </MenuItem>
                                <Divider sx={{ my: 0.5 }} />
                                <Box sx={{ px: 1.5, py: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)', fontWeight: 700 }}>
                                        Main chat
                                    </Typography>
                                </Box>
                                {isSuperAdmin(userInfo?.groups) && (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                size="small"
                                                checked={useSynapse}
                                                onChange={(_event: React.ChangeEvent<HTMLInputElement>, v: boolean) =>
                                                    setUseSynapse(v)
                                                }
                                            />
                                        }
                                        label="Personalization"
                                        sx={{ m: 0 }}
                                    />
                                )}
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={forceWebSearch}
                                            onChange={(_event: React.ChangeEvent<HTMLInputElement>, v: boolean) =>
                                                setForceWebSearch(v)
                                            }
                                        />
                                    }
                                    label="Force web search"
                                    sx={{ m: 0 }}
                                />
                                {isAnyAdmin(userInfo?.groups) && (
                                    <>
                                        <AdminGoalChecklistVisibilityControl
                                            groups={userInfo?.groups}
                                            visible={showGoalChecklist}
                                            onVisibleChange={setShowGoalChecklist}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    size="small"
                                                    checked={autoStartDiscovery}
                                                    onChange={(_event: React.ChangeEvent<HTMLInputElement>, v: boolean) =>
                                                        setAutoStartDiscovery(v)
                                                    }
                                                />
                                            }
                                            label="Auto-start Discovery"
                                            sx={{ m: 0 }}
                                        />
                                        {autoStartDiscovery && autoStartDiscoveryStatus !== 'idle' && (
                                            <Typography variant="caption" sx={{ px: 1.5, color: 'var(--color-text-secondary)' }}>
                                                Discovery status: {autoStartDiscoveryStatus}
                                            </Typography>
                                        )}
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    size="small"
                                                    checked={discoveryTestRunEnabled}
                                                    onChange={(_event: React.ChangeEvent<HTMLInputElement>, v: boolean) =>
                                                        setDiscoveryTestRunEnabled(v)
                                                    }
                                                />
                                            }
                                            label="Fresh Discovery test run"
                                            sx={{ m: 0 }}
                                        />
                                        {discoveryTestRunEnabled && (
                                            <Box sx={{ px: 1.5, pb: 1, display: 'grid', gap: 0.75 }}>
                                                <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                                                    Test run: {discoveryTestRunId}
                                                </Typography>
                                                <button
                                                    type="button"
                                                    className="scout-complete-button"
                                                    onClick={createNewDiscoveryTestRun}
                                                >
                                                    New test run
                                                </button>
                                            </Box>
                                        )}
                                    </>
                                )}
                                {canUseChatVariants && (
                                    <>
                                        <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
                                            <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)', fontWeight: 700 }}>
                                                Comparison chat
                                            </Typography>
                                        </Box>
                                        {isSuperAdmin(userInfo?.groups) && (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        size="small"
                                                        checked={useSynapseVariant}
                                                        onChange={(_event: React.ChangeEvent<HTMLInputElement>, v: boolean) =>
                                                            setUseSynapseVariant(v)
                                                        }
                                                    />
                                                }
                                                label="Personalization"
                                                sx={{ m: 0 }}
                                            />
                                        )}
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    size="small"
                                                    checked={comparisonForceWebSearch}
                                                    onChange={(_event: React.ChangeEvent<HTMLInputElement>, v: boolean) =>
                                                        setComparisonForceWebSearch(v)
                                                    }
                                                />
                                            }
                                            label="Force web search"
                                            sx={{ m: 0 }}
                                        />
                                    </>
                                )}
                                <Divider sx={{ my: 0.5 }} />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={showClassification}
                                            onChange={(_event: React.ChangeEvent<HTMLInputElement>, v: boolean) =>
                                                setShowClassification(v)
                                            }
                                        />
                                    }
                                    label="Show classification badges"
                                    sx={{ m: 0 }}
                                />
                                </Box>
                            </Popover>
                        )}
                        <textarea
                            ref={inputRef}
                            className="chat-input"
                            placeholder="How can I help?"
                            value={inputValue}
                            onChange={e => {
                                inputOriginRef.current = 'text';
                                setInputValue(e.target.value);
                            }}
                            onKeyDown={handleKeyDown}
                            onFocus={() => handleComposerFocus()}
                            rows={1}
                            disabled={isLoading || !workspacePersonaReady}
                        />
                        <button
                            className={`send-button ${isRecording ? 'recording' : ''}`}
                            onClick={() => {
                                if (isRecording) {
                                    stopVoiceRecognition();
                                } else if (inputValue.trim()) {
                                    handleSend();
                                } else {
                                    handleVoiceButtonClick();
                                }
                            }}
                            disabled={(!recordingSupported && !inputValue.trim()) || isLoading || !workspacePersonaReady}
                            aria-label={
                                isRecording
                                    ? 'Stop recording'
                                    : inputValue.trim()
                                        ? 'Send message'
                                        : 'Voice note'
                            }
                            title={
                                isRecording
                                    ? 'Stop recording'
                                    : inputValue.trim()
                                        ? 'Send message'
                                        : 'Voice note'
                            }
                        >
                            {isRecording ? <StopIcon /> : inputValue.trim() ? <SendIcon /> : <MicIcon />}
                        </button>
                    </div>
                </div>
            </div>
            {!isWorkspacePresentation && <VideoCallModal
                open={videoCallOpen}
                onClose={() => setVideoCallOpen(false)}
                persona={selectedPersona ?? null}
                onCallEnded={async (chatId) => {
                    if (!userInfo || !chatId) return;
                    if (urlChatSession !== chatId) {
                        navigateToChat(chatId, true);
                    }
                    // Force the session-load effect to rerun for this id by clearing the cache,
                    // so call utterances synced back into the chat session appear inline.
                    lastLoadedSessionId.current = null;
                    try {
                        const msgs = await getChatMessages(userInfo, chatId);
                        if (msgs) {
                            setMessages(msgs, chatId);
                            lastLoadedSessionId.current = chatId;
                        }
                    } catch (err) {
                        console.error('Failed to reload messages after call', err);
                    }
                }}
            />}
        </div>
    );
};

export default TextChatPage;
